import { Avatar as RadixAvatar } from "radix-ui";
import { avatarTone, initials } from "@bundle/core/logic/format";
import { Icon } from "./Icon.tsx";
import { useMediaSrc } from "./media.tsx";
import { cx, size as sizeVar } from "./tokens.ts";

export type AvatarProps = {
  name: string;
  src?: string;
  /** `avatar.message` (36): the message-row avatar. */
  size?: "avatar.xs" | "avatar.sm" | "avatar.md" | "avatar.lg" | "avatar.xl" | "avatar.message";
  presence?: "online" | "away";
  /** Do not disturb: a moon replaces the presence dot. */
  dnd?: boolean;
  /** What assistive tech reads for the moon ("Notifications paused"; translated). */
  dndLabel?: string;
  /** Rounded square (agents, workspaces). */
  square?: boolean;
  /** Background tone 1–8 chosen by the member (`MemberAvatar.color`); absent = derived from the name. */
  tone?: number;
  /** An emoji shown instead of the initials (`MemberAvatar.emoji`, user content). */
  emoji?: string;
};

/** Background tone 1–8 (core `Format.avatarTone`, vector-tested on every platform). */
export { avatarTone };

/**
 * A photo (`src` through the media pipeline: API file URLs get the read token), else initials or an emoji on the
 * tone. Core `Profile.avatarLook(member)` gives `src` / `tone` / `emoji` for a member.
 */
export function Avatar({ name, src, size = "avatar.md", presence, dnd, dndLabel, square, tone, emoji }: AvatarProps) {
  const px = sizeVar(size);
  const url = useMediaSrc(src);
  const bg = tone !== undefined && tone >= 1 && tone <= 8 ? Math.floor(tone) : avatarTone(name);
  return (
    <span className={cx("b-avatar", square && "b-avatar-square", `b-${size.replace(".", "-")}`)} style={{ width: px, height: px }} aria-label={name} role="img">
      <RadixAvatar.Root className="b-avatar-inner" style={{ background: `var(--b-color-avatar-${bg})` }}>
        {url === undefined ? null : <RadixAvatar.Image key={url} className="b-avatar-img" src={url} alt="" />}
        <RadixAvatar.Fallback className={cx("b-avatar-initials", emoji !== undefined && "b-avatar-emoji")} delayMs={src === undefined ? 0 : 400}>
          {emoji ?? initials(name)}
        </RadixAvatar.Fallback>
      </RadixAvatar.Root>
      {dnd === true ? (
        <span className="b-avatar-dnd" title={dndLabel} aria-label={dndLabel} role={dndLabel === undefined ? undefined : "img"} data-testid="avatar-dnd">
          <Icon name="moon" size="icon.sm" />
        </span>
      ) : presence === undefined ? null : (
        <span className={cx("b-presence", `b-presence-${presence}`)} />
      )}
    </span>
  );
}

/**
 * A group conversation's picture (Slack's group DM icon): the first two people's avatars overlapping, the second one
 * lower right with a ring in the background colour, in an `avatar.message` (36) box.
 */
export function AvatarStack({ people }: { people: Omit<AvatarProps, "size" | "presence" | "dnd" | "dndLabel">[] }) {
  const [a, b] = people;
  if (a === undefined) return null;
  return (
    <span className="b-avatar-stack" style={{ width: sizeVar("avatar.message"), height: sizeVar("avatar.message") }}>
      <Avatar {...a} size="avatar.sm" />
      {b === undefined ? null : <Avatar {...b} size="avatar.sm" />}
    </span>
  );
}
